import { getRandomInt, shuffle } from "../Shared/Helpers.tsx";
import { MagicSquareSpec, ProblemData } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";

/**
 * Magic squares (ADR 0088 §4): a 3 x 3 grid whose rows, columns and two long
 * diagonals all add to one printed total, with five cells given.
 *
 * Every 3 x 3 magic square is, with centre c and two free numbers x and y,
 *
 *     c+x    c-x-y   c+y
 *     c-x+y  c       c+x-y
 *     c-y    c+x+y   c-x
 *
 * so a square is three numbers and each cell is a linear form in them. A
 * puzzle has exactly one solution when the given cells' forms have rank 3,
 * which is a determinant, not a search.
 */

/** Each cell as [coefficient of c, of x, of y], reading order. */
export const CELL_FORMS: number[][] = [
    [1, 1, 0],
    [1, -1, -1],
    [1, 0, 1],
    [1, -1, 1],
    [1, 0, 0],
    [1, 1, -1],
    [1, 0, -1],
    [1, 1, 1],
    [1, -1, 0],
];

/** Cells printed on every square. Five of nine leaves four to find. */
export const GIVEN_CELLS = 5;

/** The cells a square has: row by row. */
export function squareFrom(c: number, x: number, y: number): number[] {
    return CELL_FORMS.map(([cc, cx, cy]) => cc * c + cx * x + cy * y);
}

function determinant(a: number[], b: number[], c: number[]): number {
    return (
        a[0] * (b[1] * c[2] - b[2] * c[1]) -
        a[1] * (b[0] * c[2] - b[2] * c[0]) +
        a[2] * (b[0] * c[1] - b[1] * c[0])
    );
}

/**
 * True when the given cells pin the square down to one solution: some three of
 * their forms are independent, so c, x and y are all determined.
 */
export function hasUniqueSolution(given: number[]): boolean {
    for (let i = 0; i < given.length; i++) {
        for (let j = i + 1; j < given.length; j++) {
            for (let k = j + 1; k < given.length; k++) {
                if (determinant(CELL_FORMS[given[i]], CELL_FORMS[given[j]], CELL_FORMS[given[k]]) !== 0) {
                    return true;
                }
            }
        }
    }
    return false;
}

/** The magic total of a square: three times its centre. */
export function magicTotal(cells: number[]): number {
    return cells[4] * 3;
}

/** True when all eight lines add to the total. */
export function isMagic(cells: number[]): boolean {
    const total = magicTotal(cells);
    const lines = [
        [0, 1, 2], [3, 4, 5], [6, 7, 8],
        [0, 3, 6], [1, 4, 7], [2, 5, 8],
        [0, 4, 8], [2, 4, 6],
    ];
    return lines.every((line) => line.reduce((sum, i) => sum + cells[i], 0) === total);
}

const squaresCache = new Map<number, number[][]>();

/**
 * Every square of nine different positive whole numbers, none above `max`.
 * Listed rather than drawn at random: at nine there are only eight of them in
 * a space of hundreds of draws, and a rejection loop would run out of attempts.
 */
export function allSquares(max: number): number[][] {
    const cached = squaresCache.get(max);
    if (cached) {
        return cached;
    }
    const found = new Map<string, number[]>();
    for (let c = 1; c <= max; c++) {
        // Every cell is c plus or minus x, y or their sum, so none can be more than c - 1 below it.
        const reach = Math.min(c - 1, max - c);
        for (let x = -reach; x <= reach; x++) {
            for (let y = -reach; y <= reach; y++) {
                const cells = squareFrom(c, x, y);
                if (cells.every((cell) => cell >= 1 && cell <= max) && new Set(cells).size === 9) {
                    found.set(cells.join(","), cells);
                }
            }
        }
    }
    const list = [...found.values()];
    squaresCache.set(max, list);
    return list;
}

/** Five cells to print, chosen so the square has one solution; null if none of a few draws does. */
export function chooseGiven(): number[] | null {
    for (let attempt = 0; attempt < 30; attempt++) {
        const given = shuffle([0, 1, 2, 3, 4, 5, 6, 7, 8]).slice(0, GIVEN_CELLS).sort((a, b) => a - b);
        if (hasUniqueSolution(given)) {
            return given;
        }
    }
    return null;
}

/**
 * How many different squares a sheet can hold at this size. Read by
 * `perPageOptions`: at nine there are eight, and a page of eight is the most
 * that can be all different - a limit on how many exist, not on space.
 */
export function distinctMagicSquares(max: number): number {
    return allSquares(max).length;
}

/** One square of the size, built from nine cells already drawn. */
function problemFrom(cells: number[], key: number): ProblemData | null {
    const given = chooseGiven();
    if (!given) {
        return null;
    }
    const magicSquare: MagicSquareSpec = { cells, given };
    return { key, numbers: [magicTotal(cells)], operator: "", magicSquare };
}

/**
 * A page-shaped set of magic squares. No two squares on a page are alike while
 * the size has enough different ones (ADR 0088 §4); across pages, squares are
 * not repeated until the size has run out of new ones, and then they are dealt
 * again, so at nine - which has only eight - every page is the same eight.
 */
export function generateMagicSquarePages(settings: ISettingsProps): ProblemData[][] | null {
    const max = settings.MagicSquareMax ?? 20;
    const all = allSquares(max);
    const perPage = Math.min(settings.NumberOfProblemsPerPage, all.length);
    if (perPage === 0) {
        return null;
    }
    const used = new Set<number>();
    const pages: ProblemData[][] = [];
    for (let page = 0; page < settings.NumberOfPages; page++) {
        const fresh = all.length - used.size;
        // Start again from the whole set once the new ones cannot fill a page.
        if (fresh < perPage) {
            used.clear();
        }
        const chosen = new Set<number>();
        while (chosen.size < perPage) {
            const index = getRandomInt(0, all.length - 1);
            if (!used.has(index)) {
                chosen.add(index);
            }
        }
        const problems: ProblemData[] = [];
        for (const index of chosen) {
            used.add(index);
            const problem = problemFrom(all[index], problems.length + 1);
            if (!problem) {
                return null;
            }
            problems.push(problem);
        }
        pages.push(problems);
    }
    return pages;
}
